import { FaroButton, FaroText, grey } from "@faro-lotv/flat-ui";
import { Stack } from "@mui/material";
import type { JSX } from "react";
import { useWorldMapStore } from "./world-map-store";

const STEPS = [
  "Click a clearly visible point in your data, e.g. a building corner.",
  "Click the same point on the map.",
  "Click a second point in your data, ideally far away from the first one.",
  "Click the same second point on the map.",
];

/** @returns A note that the world map layer is a prototype and its alignment is only stored locally */
export function WorldMapPrototypeNote(): JSX.Element {
  return (
    <FaroText variant="bodyXS" dark sx={{ color: grey[300] }}>
      Prototype: the map alignment is saved only in this browser and is not
      shared with other users.
    </FaroText>
  );
}

/** @returns The step-by-step instructions of a running world map alignment */
export function WorldMapAlignmentPanel(): JSX.Element | null {
  const picks = useWorldMapStore((state) => state.alignmentPicks);
  const undoAlignmentPick = useWorldMapStore((s) => s.undoAlignmentPick);
  const stopAlignment = useWorldMapStore((s) => s.stopAlignment);
  if (!picks) return null;

  return (
    <Stack
      aria-label="world map alignment"
      sx={{
        alignSelf: "center",
        pointerEvents: "auto",
        gap: 1,
        p: 1.5,
        maxWidth: 460,
        backgroundColor: grey[1000],
        border: `1px solid ${grey[800]}`,
        borderRadius: 1,
      }}
    >
      <FaroText variant="heading14" dark>
        Align world map – step {Math.min(picks.length + 1, STEPS.length)} of{" "}
        {STEPS.length}
      </FaroText>
      <FaroText variant="bodyM" dark>
        {STEPS[Math.min(picks.length, STEPS.length - 1)]}
      </FaroText>
      <FaroText variant="bodyS" dark sx={{ color: grey[300] }}>
        Zoom in for precise points; drag to pan. Two point pairs define the
        position and rotation of the map, its real-world scale is kept.
      </FaroText>
      <WorldMapPrototypeNote />
      <Stack direction="row" sx={{ gap: 1, justifyContent: "flex-end" }}>
        <FaroButton
          variant="ghost"
          size="s"
          dark
          disabled={picks.length === 0}
          onClick={undoAlignmentPick}
        >
          Undo point
        </FaroButton>
        <FaroButton variant="ghost" size="s" dark onClick={stopAlignment}>
          Cancel
        </FaroButton>
      </Stack>
    </Stack>
  );
}
